import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DownloadsProvider } from './context/DownloadsContext';
import Header from './components/Header';
import SidebarContainer from './components/SidebarContainer';
import ScrollToTop from './components/ScrollToTop';
import Downloads from './components/Downloads';
import Store from './pages/Store';
import Bundles from './pages/Bundles';
import Creators from './pages/Creators';
import CreatorDetail from './pages/CreatorDetail';
import CreatorDashboard from './pages/CreatorDashboard';
import CreatorRoute from './components/CreatorRoute';
import Library from './pages/Library';
import Account from './pages/Account';
import PurchaseShards from './pages/PurchaseShards';
import PurchaseShardsReturn from './pages/PurchaseShardsReturn';
import GameDetail from './pages/GameDetail';
import Search from './pages/Search';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import InfoPageRoute from './components/InfoPageRoute';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
// QUIZ FEATURE - Comment out to disable quiz
// import Quiz from './pages/Quiz';
import AgeVerification from './components/AgeVerification';
import useFirstVisit from './hooks/useFirstVisit';
import usePageTracking from './hooks/usePageTracking';

// Component to handle page tracking
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  const { isFirstVisit, markVisited } = useFirstVisit();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <AuthProvider>
      <DownloadsProvider>
        <Router>
          <PageTracker />
          <ScrollToTop />
          <div className="w-full min-h-screen bg-dark-bg overflow-x-hidden relative">
            {/* Age Verification - Blocks all content until confirmed */}
            {isFirstVisit ? (
              <AgeVerification onConfirm={markVisited} />
            ) : (
              <>
                <Routes>
              {/* Auth Routes - No Header */}
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              
              {/* Admin Routes - No Header */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />

              {/* Creator dashboard - standalone, no header or sidebar */}
              <Route
                path="/creator/dashboard"
                element={
                  <CreatorRoute>
                    <CreatorDashboard />
                  </CreatorRoute>
                }
              />
              
              {/* Info and legal pages: header only, no sidebar */}
              <Route path="/about" element={
                <InfoPageRoute>
                  <About />
                </InfoPageRoute>
              } />
              <Route path="/privacy" element={
                <InfoPageRoute>
                  <PrivacyPolicy />
                </InfoPageRoute>
              } />
              <Route path="/terms" element={
                <InfoPageRoute>
                  <TermsOfService />
                </InfoPageRoute>
              } />

              {/* Protected Routes - With Header and Sidebar */}
              <Route path="/account" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <Account />
                  </div>
                </div>
              </>} />
              <Route path="/purchase-shards" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <PurchaseShards />
                  </div>
                </div>
              </>} />
              <Route path="/purchase-shards/return" element={<PurchaseShardsReturn />} />
              <Route path="/game/:id" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <GameDetail />
                  </div>
                </div>
              </>} />
              <Route path="/search" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <Search />
                  </div>
                </div>
              </>} />
              {/* QUIZ FEATURE - Comment out this route to disable quiz */}
              {/* <Route path="/quiz" element={<Quiz />} /> */}
              {/* END QUIZ FEATURE */}
              <Route path="/" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <Store />
              </>} />
              <Route path="/bundles" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <Bundles />
                  </div>
                </div>
              </>} />
              <Route path="/creators" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <Creators />
                  </div>
                </div>
              </>} />
              <Route path="/library" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <Library />
                  </div>
                </div>
              </>} />
              <Route path="/creator/:id" element={<>
                <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
                <SidebarContainer isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <div className="min-h-screen pt-24 md:pt-32 pl-0 md:ml-[280px] pr-2 lg:pr-5 overflow-x-hidden">
                  <div className="w-full max-w-full">
                    <CreatorDetail />
                  </div>
                </div>
              </>} />
                </Routes>
                <Downloads />
              </>
            )}
          </div>
        </Router>
      </DownloadsProvider>
    </AuthProvider>
  );
}

export default App;
